/**
 * Partner two-factor step of sign-in (C04-002, QM-D15). After a sign-in link or an old session lands in
 * `enrol_required` or `pending`, the platform blocks every non-identity route, so this screen is the only way on:
 * enrol an authenticator (QR + code, then one-time recovery codes), or verify with a code, a passkey or a recovery
 * code. Mirrors the customer sign-in; passkey buttons show only while the passkey API answers (flag identity.passkeys).
 */
import { useState, type FormEvent } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { Field } from '../kit/ui';
import { passkeyErrorKey, passkeySecondFactor, passkeysSupported, registerPasskey } from '../../../lib/passkeys';
import { usePartnerApi } from './layout';

export type TwoFactorStep = 'verify' | 'enrol';

/** consume / me -> which two-factor step comes next ('mfa' and 'enrol_mfa' are the API values). */
export function twoFactorStepFor(next: string | undefined): TwoFactorStep | null {
  if (next === 'mfa' || next === 'mfa_verify' || next === 'totp' || next === 'pending') return 'verify';
  if (next === 'enrol_mfa' || next === 'enrol_required') return 'enrol';
  return null;
}

const digits = (v: string) => v.replace(/[٠-٩۰-۹]/g, (c) => String(c.charCodeAt(0) - (c <= '٩' ? 0x660 : 0x6f0))).replace(/\D/g, '');

export function TwoFactor({ initial, methods, onDone }: { initial: TwoFactorStep; methods?: string[]; onDone: () => void }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [step, setStep] = useState<'verify' | 'enrol' | 'recovery'>(initial);
  const [code, setCode] = useState('');
  const [factor, setFactor] = useState<{ factorId: string; secret: string; otpauthUri: string } | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const usable = methods?.length ? methods : ['totp'];
  const passkeyUi = passkeysSupported() && usable.includes('passkey');
  const fail = (key: string) => setError(t(key));

  const guard = async (run: () => Promise<void>, errorKey = 'portal.partners.twofa.codeInvalid') => {
    if (busy) return;
    setBusy(true); setError(null);
    try { await run(); } catch { fail(errorKey); } finally { setBusy(false); }
  };
  const verify = (e: FormEvent) => { e.preventDefault(); void guard(async () => { await api.verifyCode(code); onDone(); }); };
  const recover = (e: FormEvent) => { e.preventDefault(); void guard(async () => { await api.recover(recoveryCode.trim()); setRecoveryCode(''); setCode(''); setShowRecovery(false); setStep('enrol'); }); };
  const beginEnrol = () => void guard(async () => { setFactor(await api.totpEnrol()); setCode(''); }, 'portal.kit.error');
  const confirm = (e: FormEvent) => { e.preventDefault(); if (!factor) return; void guard(async () => { const r = await api.totpConfirm(factor.factorId, code); setCodes(r.recoveryCodes); setFactor(null); setCode(''); setStep('recovery'); }); };
  const passkeyVerify = () => void guard(async () => { await passkeySecondFactor(); onDone(); }, 'portal.partners.twofa.codeInvalid');
  const passkeyEnrol = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await registerPasskey();
      if (r.recoveryCodes?.length) { setCodes(r.recoveryCodes); setFactor(null); setStep('recovery'); } else onDone();
    } catch (err) { setError(t(passkeyErrorKey(err))); } finally { setBusy(false); }
  };

  const codeField = (hint?: string) => (
    <Field label={t('portal.partners.twofa.code')} hint={hint} error={error}>
      {(id, describedBy) => <input id={id} aria-describedby={describedBy} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(digits(e.target.value))} />}
    </Field>
  );

  if (step === 'recovery') {
    return (
      <div data-testid="partner-2fa-recovery">
        <Text>{t('portal.partners.twofa.recoveryTitle')}</Text>
        <Text tone="muted">{t('portal.partners.twofa.recoveryIntro')}</Text>
        <ul dir="ltr">{codes.map((rc) => <li key={rc}>{rc}</li>)}</ul>
        <label><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> {t('portal.partners.twofa.recoverySaved')}</label>
        <Button type="button" variant="primary" disabled={!saved} onClick={onDone}>{t('portal.partners.twofa.continue')}</Button>
      </div>
    );
  }
  if (step === 'enrol') {
    return (
      <div data-testid="partner-2fa-enrol">
        {!factor ? (
          <>
            <Text>{t('portal.partners.twofa.enrolIntro')}</Text>
            <Button type="button" variant="primary" disabled={busy} onClick={beginEnrol} data-testid="partner-2fa-start">{t('portal.partners.twofa.enrolStart')}</Button>
            {passkeyUi ? <Button type="button" disabled={busy} onClick={() => void passkeyEnrol()} data-testid="partner-2fa-passkey-enrol">{t('portal.partners.twofa.passkeyEnrol')}</Button> : null}
            {error ? <Text role="alert">{error}</Text> : null}
          </>
        ) : (
          <>
            <Text>{t('portal.partners.twofa.scan')}</Text>
            <div data-testid="partner-2fa-qr"><QRCodeSVG value={factor.otpauthUri} size={208} marginSize={4} title={t('portal.partners.twofa.qrTitle')} /></div>
            <Button type="button" aria-expanded={showKey} aria-controls="partner-2fa-key" onClick={() => setShowKey(!showKey)}>{showKey ? t('portal.partners.twofa.hideKey') : t('portal.partners.twofa.showKey')}</Button>
            {showKey ? <div id="partner-2fa-key"><code dir="ltr" tabIndex={0}>{factor.secret}</code></div> : null}
            <Text tone="muted">{t('portal.partners.twofa.secret')}</Text>
            <form onSubmit={confirm} noValidate>{codeField()}<Button type="submit" variant="primary" disabled={busy || code.length !== 6}>{t('portal.partners.twofa.confirm')}</Button></form>
          </>
        )}
      </div>
    );
  }
  return (
    <div data-testid="partner-2fa-verify">
      {usable.includes('totp') ? (
        <form onSubmit={verify} noValidate>
          <Text>{t('portal.partners.twofa.verifyIntro')}</Text>
          {codeField()}
          <Button type="submit" variant="primary" disabled={busy || code.length !== 6}>{t('portal.partners.twofa.verify')}</Button>
        </form>
      ) : null}
      {passkeyUi ? <Button type="button" disabled={busy} onClick={passkeyVerify} data-testid="partner-2fa-passkey">{t('portal.partners.twofa.passkeyVerify')}</Button> : null}
      {!usable.includes('totp') && error ? <Text role="alert">{error}</Text> : null}
      {showRecovery ? (
        <form onSubmit={recover} noValidate id="partner-2fa-recovery-form" data-testid="partner-2fa-recovery-form">
          <Field label={t('portal.partners.twofa.recoveryCode')}>
            {(id, describedBy) => <input id={id} aria-describedby={describedBy} autoComplete="off" spellCheck={false} dir="ltr" maxLength={32} required autoFocus value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} />}
          </Field>
          <Button type="submit" disabled={busy || recoveryCode.trim().length < 8}>{t('portal.partners.twofa.recoveryUse')}</Button>
        </form>
      ) : (
        <Button type="button" aria-expanded={false} aria-controls="partner-2fa-recovery-form" onClick={() => setShowRecovery(true)} data-testid="partner-2fa-recovery-toggle">{t('portal.partners.twofa.recoveryToggle')}</Button>
      )}
    </div>
  );
}
